import { Component, Inject, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { OuiSelectOption } from 'omnium-ui/form-field';
import { OuiSnackbarService } from 'omnium-ui/snackbar';
import { DEROGATION_VALUE } from 'projects/box-lib/src/lib/models/box-additionnal-data';
import { OperationSpecificData } from 'projects/box-lib/src/lib/models/donees-specifiques.model';
import { Subscription } from 'rxjs';
import {
  FondEvenementiel,
  BackOfficeService,
  Operation,
  TransactionPersonnelleEnum,
  TypeSignaturePartenaire,
  UpdateOperationInput,
} from '../../../models/generated/graphql';
import { FondsEvenementielsService } from '../../../services/fonds-evenementiels.service';
import { OperationsService } from '../../../services/operations.service';
import { OptionsService } from '../../../services/options.service';
import { EnveloppeProduitService } from '../../../services/enveloppe-produit.service';

export type OperationChamp =
  | 'typeSignaturePartenaire' //inclus aussi transmission
  | 'fondsEvenementiels'
  | 'montant'
  | 'modePaiement'
  | 'transaction-personnelle'
  | 'objetModification'
  | 'tauxFrais' // type et impact
  | 'contrepartieName';

export type OperationUpdateDialogData = { operation: Operation; champ: OperationChamp; currentValue: any };

@Component({
    selector: 'lib-operation-update-dialog',
    templateUrl: './operation-update-dialog.component.html',
    styleUrls: ['./operation-update-dialog.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OperationUpdateDialogComponent implements OnInit {
  signatureOptions: OuiSelectOption<TypeSignaturePartenaire>[];
  transmissionListOptions: OuiSelectOption<string>[];

  fondsEvenementielsOptions: OuiSelectOption[];
  modePaiementOptions: OuiSelectOption<string>[];
  transactionPersonnelleOptions: OuiSelectOption<TransactionPersonnelleEnum>[];
  objetModificationOptions: OuiSelectOption<string>[];
  typeTauxFraisOptions: OuiSelectOption<string>[];
  impactTauxFraisOptions: OuiSelectOption<string>[];
  control = new FormControl<any>(null, Validators.required);
  secondaryControl = new FormControl<any>(null, Validators.required);
  showSecondaryControl = false;
  toggleControl = new FormControl<boolean>(false);

  habiliationSet: Set<number>;
  currentValue: any;
  isPsiTransaction = false;
  transactionContextReady = false;

  donneesSpecifiques: OperationSpecificData = {};
  canSave: boolean = true;

  controlSub: Subscription;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: OperationUpdateDialogData,
    private dialogRef: MatDialogRef<OperationUpdateDialogComponent>,
    private optionsService: OptionsService,
    private operationsService: OperationsService,
    private fondsEvenementielsService: FondsEvenementielsService,
    private snackbarService: OuiSnackbarService,
    private enveloppeProduitService: EnveloppeProduitService
  ) {
    const habilitationId = data.operation.produit?.habilitationNavigation?.id ?? data.operation.produit?.habilitation;
    this.habiliationSet = new Set(habilitationId == null ? [] : [habilitationId]);
    this.currentValue = data.currentValue;
  }
  get withCoInvestisseur(): boolean {
    const operation = this.data.operation;
    return operation.coInvestisseurId != null || operation.coInvestisseur?.id != null;
  }

  ngOnDestroy(): void {
    this.controlSub?.unsubscribe();
  }
  ngOnInit(): void {
    if (this.data.champ === 'transaction-personnelle') {
      this.canSave = false;
      this.control.setValue(this.currentValue ?? TransactionPersonnelleEnum.Non);
      // Utiliser la configuration de l'operation, comme le serveur.
      const enveloppeCode = this.data.operation.operationConfig?.enveloppeId ??
        this.data.operation.produit?.enveloppeNavigation?.code;
      const servicePromise = enveloppeCode == null
        ? Promise.reject(new Error('Enveloppe manquante'))
        : this.enveloppeProduitService.getServiceForEnveloppe(enveloppeCode);
      servicePromise.then(service => {
        this.isPsiTransaction = service === BackOfficeService.Psi;
        this.transactionContextReady = true;
        this.canSave = true;
      }).catch(() => {
        this.canSave = false;
        this.snackbarService.open("Impossible de determiner le service de cette operation", 'error');
      });
    }
    this.donneesSpecifiques = this.operationsService.parseSpecificData(this.data.operation.donneesSpecifiques);
    switch (this.data.champ) {
      case 'montant':
        this.initMontant();
        break;
      case 'contrepartieName':
        this.initContrepartieName();
        break;
      case 'typeSignaturePartenaire':
        this.initTypeSignature();
        break;
      case 'fondsEvenementiels':
        this.initFondsEvenementiels();
        break;

      case 'modePaiement':
        this.initModePaiement();
        break;
      case 'objetModification':
        this.initObjetModification();
        break;
      case 'tauxFrais':
        this.initTauxFrais();
        break;
    }
  }

  initTypeSignature() {
    this.signatureOptions = this.optionsService.getTypeSignaturePartenaireOptions();
    this.transmissionListOptions = this.optionsService.getTransmissionOptions();
    if (this.data.operation.typeSignaturePartenaire === TypeSignaturePartenaire.Manuscrite) {
      this.setSecondaryControl(this.donneesSpecifiques.transmissionOriginaux);
    }
    this.controlSub = this.control.valueChanges.subscribe(value => {
      if (value === TypeSignaturePartenaire.Manuscrite) {
        this.setSecondaryControl(this.donneesSpecifiques.transmissionOriginaux);
      } else {
        this.showSecondaryControl = false;
        this.secondaryControl.setValue(null);
        this.secondaryControl.setValidators(null);
      }
    });

    this.control.setValue(this.data.operation.typeSignaturePartenaire);
  }
  initAutoSouscription() {
    this.toggleControl.setValue(this.data.currentValue);
  }
  initMontant() {
    this.control = new FormControl<number>(this.data.currentValue, Validators.required);
    this.control.valueChanges.subscribe(value => {
      this.canSave = value && value >= 0;
    });
  }
  initContrepartieName() {
    this.control = new FormControl<string>(this.data.currentValue, Validators.required);
  }
  initModePaiement() {
    this.modePaiementOptions = this.optionsService.getModePaiementOptions();
    this.control.setValue(this.data.currentValue);
  }
  initObjetModification() {
    this.objetModificationOptions = this.optionsService.getObjetModificationOptions();
    this.control.setValue(this.data.currentValue);
  }
  initTauxFrais() {
    this.typeTauxFraisOptions = this.optionsService.getTypeTauxFraisOptions() as OuiSelectOption<string>[];
    this.impactTauxFraisOptions = this.optionsService.getImpactTauxFraisOptions() as OuiSelectOption<string>[];
    this.showSecondaryControl = this.donneesSpecifiques.typeTauxFrais === DEROGATION_VALUE;
    if (this.donneesSpecifiques.typeTauxFrais === DEROGATION_VALUE) {
      this.setSecondaryControl(this.donneesSpecifiques.impactDiminutionFrais);
    }
    this.controlSub = this.control.valueChanges.subscribe(value => {
      if (value === DEROGATION_VALUE) {
        this.setSecondaryControl(this.donneesSpecifiques.impactDiminutionFrais);
      } else {
        this.showSecondaryControl = false;
        this.secondaryControl.setValidators(null);
        this.secondaryControl.setValue(null);
      }
    });
    this.control.setValue(this.donneesSpecifiques.typeTauxFrais);
  }
  private setSecondaryControl(value: string | null | undefined) {
    this.secondaryControl.setValidators([Validators.required]);
    this.secondaryControl.setValue(value);
    this.secondaryControl.updateValueAndValidity();
    this.showSecondaryControl = true;
  }
  async initFondsEvenementiels() {
    const fondsEvenementiels = await this.fondsEvenementielsService.getfondEvenementielsByProductId(
      this.data.operation.produit!.id
    );

    this.fondsEvenementielsOptions = fondsEvenementiels.map(
      fe =>
        <OuiSelectOption>{
          label: fe.libelle,
          value: fe.id,
        }
    );

    this.control.setValue(this.data.currentValue.map((fe: FondEvenementiel) => fe.id));
  }

  updateTransactionPersonelleStatut(transaction: TransactionPersonnelleEnum) {
    this.control.setValue(transaction);
  }

  async saveOperation(): Promise<void> {
    const updateInput: UpdateOperationInput = {
      operationId: this.data.operation.id,
      operationGroupId: this.data.operation.operationGroupId ?? 0,
    };

    // Update the field
    switch (this.data.champ) {
      case 'typeSignaturePartenaire':
        updateInput.typeSignaturePartenaire = this.control.value;
        this.donneesSpecifiques.transmissionOriginaux = this.secondaryControl.value;
        break;
      case 'fondsEvenementiels':
        this.donneesSpecifiques.fondEvenementielIds = this.control.value;
        break;
      case 'transaction-personnelle':
        updateInput.transactionPersonnelleStatut = this.control.value;
        break;
      case 'montant':
        this.donneesSpecifiques.montant = this.control.value;
        break;
      case 'contrepartieName':
        this.donneesSpecifiques.contrepartieName = this.control.value;
        break;
      case 'modePaiement':
        this.donneesSpecifiques.modePaiement = this.control.value;
        break;
      case 'objetModification':
        this.donneesSpecifiques.objetModification = this.control.value;
        break;
      case 'tauxFrais':
        this.donneesSpecifiques.typeTauxFrais = this.control.value;
        this.donneesSpecifiques.impactDiminutionFrais = this.secondaryControl.value;
        break;
    }

    updateInput.donneesSpecifiques = JSON.stringify(this.donneesSpecifiques);

    try {
      const result = await this.operationsService.updateOperation(updateInput);
      this.snackbarService.open("L'opération a bien été mise à jour", 'success');
      this.dialogRef.close(result);
    } catch (error) {
      this.snackbarService.open("Une erreur s'est produite durant la mise à jour de l'opération", 'error');
    }
  }
}
